import { Injectable } from '@angular/core';
import Cookies from 'js-cookie';
import { Store } from '@ngrx/store';
import { updateGdprConsent } from '../../../store/global/core-actions';
import { selectGdprConsent } from '../../../store/global/core-selectors';
import { MatDialog } from '@angular/material/dialog';
import { ConsentPopupComponent } from './consent-popup/consent-popup.component';
import { skip, take } from 'rxjs/operators';
import { GtagService } from '../../../core/gx-gtag/gtag.service';

export interface GdprConsent {
  consents: {
    analytics: boolean,
    essential: boolean,
  };
}

export const CONSENT_COOKIE_NAME = 'gdpr_consent';

@Injectable({
  providedIn: 'root',
})
export class ConsentService {
  public consent$ = this.store.select(selectGdprConsent);

  constructor(private store: Store,
              private dialog: MatDialog,
              private gtagService: GtagService) {
    // will only run if at least one class injects this service https://github.com/angular/angular/issues/25633
    // since this is a singleton service (providedIn: 'root'), will be called only once
    this.init();
  }

  /**
   * Generate JSON string.
   * @param val
   * @private
   */
  private getJsonString(val: any) {
    let jsonString: string;

    try {
      jsonString = JSON.stringify(val);
    } catch (e) {
      console.error(e);
    }

    return jsonString;
  }

  /**
   * Parse JSON string.
   * @param val
   * @private
   */
  private parseJsonString(val: any): any {
    let parsedJsonString: any;

    try {
      parsedJsonString = JSON.parse(val);
    } catch (e) {
      console.error(e);
    }

    return parsedJsonString;
  }

  /**
   * Read cookie value from local storage and update the value in Store. Set up a listener to
   * consent changes and update gtag settings accordingly.
   * @private
   */
  private init() {
    const cookie = this.getConsentCookie();

    if (cookie) {
      this.updateStoreValue(cookie);
    } else console.info('GDPR cookie not found');

    this.consent$.pipe(
        skip(1), // default consent params are set when gtag script is loaded
    ).subscribe((v) => {
      if (v) {
        this.gtagService.updateConsent(v);
      }
    });
  }

  /**
   * Update gdprConsent Store value.
   * @param consent
   * @private
   */
  private updateStoreValue(consent: GdprConsent) {
    this.store.dispatch(updateGdprConsent({ gdprConsent: consent }));
  }

  /**
   * Set GDPR cookie value in local storage.
   * @param consentCookie
   * @private
   */
  private updateConsentCookie(consentCookie: GdprConsent) {
    let consentCookieJson = this.getJsonString(consentCookie);

    if (consentCookieJson) {
      Cookies.set(CONSENT_COOKIE_NAME, consentCookieJson);
    }
  }

  /**
   * Read cookie from local storage.
   */
  public getConsentCookie(): GdprConsent {
    const consentCookie = Cookies.get(CONSENT_COOKIE_NAME);
    return consentCookie ? this.parseJsonString(consentCookie) : null;
  }

  /**
   * Update consent. Makes sure the cookie and Store value are in sync.
   * @param consent
   */
  public updateConsent(consent: GdprConsent) {
    this.updateConsentCookie(consent);
    this.updateStoreValue(consent);
  }

  /**
   * Open GDPR consent popup.
   */
  public openPopup() {
    this.consent$.pipe(take(1)).subscribe((consent) => {
      this.dialog.open(ConsentPopupComponent, {
        data: consent,
        width: '448px',
      }).afterClosed()
          .subscribe((consent) => {
            if (consent) {
              this.updateConsent(consent);
            }
          });
    });
  }
}
